import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "../components/Button";
import Card from "../components/Card";
import { Grid, Stack } from "../components/Layout";
import PageShell from "../components/PageShell";
import { Heading, Text } from "../components/Typography";
import { ContactIcon } from "./LearnMoreIcons";

const contactLinks = [
  {
    id: "email",
    label: "Email",
    href: "mailto:jacksonaden@gmail.com",
    display: "jacksonaden@gmail.com",
    external: false,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/jackson-aden-a429a12b3/",
    display: "linkedin.com/in/jackson-aden-a429a12b3",
    external: true,
  },
  {
    id: "github",
    label: "GitHub",
    href: "https://github.com/jacksonaden",
    display: "github.com/jacksonaden",
    external: true,
  },
];

const galleryImages = [
  {
    src: "/learn-more/screenshot-1.png",
    alt: "Sentiment analysis tool dashboard",
    caption: "Dashboard view",
    filename: "screenshot-1.png",
  },
  {
    src: "/learn-more/screenshot-2.png",
    alt: "Sentiment analysis results",
    caption: "Analysis results",
    filename: "screenshot-2.png",
  },
  {
    src: "/learn-more/screenshot-3.png",
    alt: "Project demo or architecture",
    caption: "Project demo",
    filename: "screenshot-3.png",
  },
];

const Hero = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const ProfileImg = styled.img`
  width: ${({ theme }) => theme.layout.profileSize};
  height: ${({ theme }) => theme.layout.profileSize};
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radii.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.layout.profileSize};
  height: ${({ theme }) => theme.layout.profileSize};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.card};
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  text-align: center;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const GalleryPlaceholder = styled(Placeholder)`
  width: 100%;
  height: ${({ theme }) => theme.layout.galleryHeight};
`;

const GalleryFigure = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const GalleryImg = styled.img`
  width: 100%;
  height: ${({ theme }) => theme.layout.galleryHeight};
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radii.control};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const ContactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ContactItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const ContactLabel = styled.span`
  min-width: ${({ theme }) => theme.spacing(8)};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const IconWrap = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SectionCard = styled(Card)`
  width: 100%;
`;

function ProfileImage() {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <Placeholder aria-hidden="true">
        <span>Add image: profile.jpg</span>
      </Placeholder>
    );
  }

  return (
    <ProfileImg
      src="/learn-more/profile.jpg"
      alt="Jackson Aden"
      onError={() => setFailed(true)}
    />
  );
}

function GalleryItem({ image }) {
  const [failed, setFailed] = useState(false);

  return (
    <GalleryFigure>
      {failed ? (
        <GalleryPlaceholder aria-hidden="true">
          <span>Add image: {image.filename}</span>
        </GalleryPlaceholder>
      ) : (
        <GalleryImg
          src={image.src}
          alt={image.alt}
          onError={() => setFailed(true)}
        />
      )}
      {image.caption && (
        <Text as="figcaption" $muted $size="sm">
          {image.caption}
        </Text>
      )}
    </GalleryFigure>
  );
}

function LearnMore() {
  const navigate = useNavigate();

  return (
    <PageShell>
      <Stack $gap={4}>
        <SectionCard padding={4}>
          <Hero>
            <ProfileImage />
            <Stack $gap={1}>
              <Heading $size="2xl">Jackson Aden</Heading>
              <Text $muted>Project creator</Text>
            </Stack>
          </Hero>
        </SectionCard>

        <SectionCard padding={4}>
          <Stack $gap={3}>
            <Text>
              I&apos;m a backend developer with a software engineering degree
              and an emphasis on data science and software architecture. I love
              solving problems, and I love seeing things get done!
            </Text>
            <Text>
              I also love researching machine learning models, and I&apos;ve
              made a lot of implementation prototypes. I wanted to make something
              for small businesses.
            </Text>
            <Text>
              I tried starting my own business back in 2024, but it was hard to
              check my DMs daily and understand how people felt about my service.
              Over time, I&apos;ve learned more about implementing machine
              learning models, and wanted to make a website that helps in reading
              lots of DMs at once!
            </Text>
            <Text>
              There is still lots more that needs to get done before it&apos;s a
              finished product, but I&apos;ll keep deploying what I have and take
              whatever feedback I can get!
            </Text>
          </Stack>
        </SectionCard>

        <SectionCard padding={4}>
          <Stack $gap={3}>
            <Heading as="h2" $size="xl">
              Project snapshots
            </Heading>
            <Grid
              $gap={3}
              $columns="repeat(auto-fit, minmax(200px, 1fr))"
              aria-label="Project images"
            >
              {galleryImages.map((image) => (
                <GalleryItem key={image.filename} image={image} />
              ))}
            </Grid>
          </Stack>
        </SectionCard>

        <SectionCard padding={4}>
          <Stack $gap={3}>
            <Heading as="h2" $size="xl">
              Contact
            </Heading>
            <ContactList>
              {contactLinks.map((link) => (
                <ContactItem key={link.id}>
                  <IconWrap>
                    <ContactIcon id={link.id} />
                  </IconWrap>
                  <ContactLabel>{link.label}</ContactLabel>
                  <a
                    href={link.href}
                    {...(link.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {link.display}
                  </a>
                </ContactItem>
              ))}
            </ContactList>
          </Stack>
        </SectionCard>

        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate("../")}
        >
          Back to home page
        </Button>
      </Stack>
    </PageShell>
  );
}

export default LearnMore;
